import Decimal from "decimal.js";
import Enumerable from "linq";
import Moment from "moment";
import {
  IConfiguration,
  IDangerZoneDocumentEditorTimeMeasurementObjectSetConfiguration,
} from "../../../../domain/configuration";
import { IDocumentEditable } from "../IDocumentEditable";
import { TextBoxItem } from "../../../../domain/document/items";
import { LoggerFactory } from "../../../../utilities/logger";

/**
 * 国がん中央AYAのV3移行のための特別対応
 */
export class EditTimeApplicator {
  constructor(private _document: IDocumentEditable, private _configuration: IConfiguration) {
  }
  private readonly _logger = LoggerFactory.getLogger();

  public apply(): void {
    const configuration = this.getObjectSetConfiguration(this._document.source.attributes.format.masterCode);
    if (!configuration) { return; }

    this._logger.info("計測した入力時間をオブジェクトに格納します。");

    const firstClickDateTime = this.getFirstClickDateTime();
    const elapsedTimeAsSeconds = this.getElapsedTimeAsSeconds(firstClickDateTime, new Date());
    
    // 続紙は対象外
    const itemSetId = 0;
    for (const item of this._document.itemSets[itemSetId].items) {
      if (!(item instanceof TextBoxItem) || !item.name || !item.isFreeInputable) { continue; }
      if (item.name === configuration.elapsedTimeAsSecondsObjectName) {
        // 合算した経過時間（秒）
        const currentText = item.text();
        const newValue = new Decimal(!!currentText ? parseInt(currentText, 10) : 0).add(new Decimal(elapsedTimeAsSeconds)).toString();
        this._logger.debug("合算した経過時間（秒）：" + newValue);
        this.updateItem(itemSetId, item, newValue);
      } else if (item.name === configuration.startDateTimesObjectName) {
        // 記入開始日時
        const newValue = this.appendDateTimeText(item.text(), firstClickDateTime);
        this._logger.debug("記入開始日時：" + newValue);
        this.updateItem(itemSetId, item, newValue);
      } else if (item.name === configuration.endDateTimesObjectName) {
        // 記入完了日時
        const endDateTime = new Date(firstClickDateTime.getTime() + (elapsedTimeAsSeconds * 1000)); // WebEditorと同様の処理にしている
        const newValue = this.appendDateTimeText(item.text(), endDateTime);
        this._logger.debug("記入完了日時：" + newValue);
        this.updateItem(itemSetId, item, newValue);
      }
    }
  }

  private getFirstClickDateTime(): Date {
    if (this._document.source.attributes.editOperation.firstClickDateTime) {
      return this._document.source.attributes.editOperation.firstClickDateTime;
    }
    this._logger.warn("記入開始日時が格納されていません。");
    return new Date();
  }

  private appendDateTimeText(currentText: string, date: Date): string {
    const appendText = Moment(date).format("YYYY/MM/DD HH:mm:ss");
    return !!currentText
      ? currentText + "|" + appendText
      : appendText;
  }

  private updateItem(itemSetId: number, item: TextBoxItem, newValue: string): void {
    const cloned = item.clone() as TextBoxItem;
    cloned.value = newValue;
    cloned.adjustObjectHeight();
    cloned.adjustFontSize();
    this._document.updateItem(cloned, {
      itemSetId,
    });
  }

  private getElapsedTimeAsSeconds(start: Date, end: Date): number {
    return Math.floor((end.getTime() - start.getTime()) / 1000);
  }

  private getObjectSetConfiguration(masterCode: string): IDangerZoneDocumentEditorTimeMeasurementObjectSetConfiguration {
    if (!masterCode) { return null; }
    return Enumerable.from(this._configuration.dangerZone.document.editor.timeMeasurement.objectSets)
      .where(s => (new RegExp(s.masterCodeExpression, "i")).test(masterCode))
      .firstOrDefault();
  }
}
